import { useState } from "react";
import { Phone, Mail, CheckCircle2, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Field, TextInput, TextArea, validateCommon } from "../components/FormControls";
import { submitContact } from "../lib/api";
import { CONTACT } from "../data/content";

const EMPTY = { name: "", institution: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validateCommon(values, ["name", "email", "message"]);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    try {
      await submitContact(values);
      setDone(true);
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="bg-ivory" aria-label="Contact us">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold-700">Contact Us</p>
          <h1 className="mt-3 font-serif text-4xl font-bold tracking-tight text-navy sm:text-5xl" data-testid="contact-heading">
            Meet With Us
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
            Partner with CASIA to co-design a curriculum journey for your school. Reach us directly or send an enquiry. Every message reaches our team at {CONTACT.email}.
          </p>

          <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_1.2fr]">
            {/* Details */}
            <div className="space-y-4">
              <a
                href="tel:+918428255599"
                data-testid="contact-phone-chennai"
                className="flex items-center gap-4 rounded-lg border border-slate-200 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-lg"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-navy">
                  <Phone className="h-5 w-5 text-gold" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-500">Chennai</span>
                  <span className="block font-semibold text-navy">{CONTACT.chennai}</span>
                </span>
              </a>
              <a
                href="tel:+919071655599"
                data-testid="contact-phone-bangalore"
                className="flex items-center gap-4 rounded-lg border border-slate-200 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-lg"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-navy">
                  <Phone className="h-5 w-5 text-gold" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-500">Bangalore</span>
                  <span className="block font-semibold text-navy">{CONTACT.bangalore}</span>
                </span>
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email"
                className="flex items-center gap-4 rounded-lg border border-slate-200 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-lg"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-navy">
                  <Mail className="h-5 w-5 text-gold" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-500">Email</span>
                  <span className="block font-semibold text-navy">{CONTACT.email}</span>
                </span>
              </a>
              <div className="flex items-start gap-3 rounded-lg bg-gold-light p-5 text-sm text-slate-700" data-testid="contact-address-card">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-gold-700" aria-hidden="true" />
                <div>
                  <p className="font-bold uppercase tracking-wider text-navy">Corporate Office</p>
                  <p className="mt-1 leading-relaxed">
                    Romar House, No. 15/2, Second Floor, Jaganathan Road, Nungambakkam, Chennai 600 034
                  </p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Branch offices: Bangalore | Hyderabad
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              {!done ? (
                <>
                  <h2 className="font-serif text-2xl font-semibold text-navy">Send an Enquiry</h2>
                  <p className="mt-1 text-sm text-slate-600">Fields marked * are required.</p>
                  <form onSubmit={onSubmit} noValidate className="mt-6 space-y-4" data-testid="contact-form">
                    <Field label="Name" htmlFor="contact-name" required error={errors.name}>
                      <TextInput id="contact-name" data-testid="contact-name-input" value={values.name} onChange={set("name")} autoComplete="name" />
                    </Field>
                    <Field label="Institution / School Name" htmlFor="contact-institution" error={errors.institution}>
                      <TextInput id="contact-institution" data-testid="contact-institution-input" value={values.institution} onChange={set("institution")} autoComplete="organization" />
                    </Field>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Email" htmlFor="contact-email" required error={errors.email}>
                        <TextInput id="contact-email" type="email" data-testid="contact-email-input" value={values.email} onChange={set("email")} autoComplete="email" />
                      </Field>
                      <Field label="Phone" htmlFor="contact-phone" error={errors.phone}>
                        <TextInput id="contact-phone" type="tel" data-testid="contact-phone-input" value={values.phone} onChange={set("phone")} autoComplete="tel" />
                      </Field>
                    </div>
                    <Field label="Message / Requirements" htmlFor="contact-message" required error={errors.message}>
                      <TextArea id="contact-message" data-testid="contact-message-input" value={values.message} onChange={set("message")} />
                    </Field>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit-button"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-navy px-4 py-3 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-navy-700 disabled:opacity-60"
                    >
                      {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                      {submitting ? "Sending…" : "Submit"}
                    </button>
                  </form>
                </>
              ) : (
                <div className="py-10 text-center" data-testid="contact-success">
                  <CheckCircle2 className="mx-auto h-14 w-14 text-gold" aria-hidden="true" />
                  <h2 className="mt-4 font-serif text-2xl font-semibold text-navy">Thank you for reaching out.</h2>
                  <p className="mx-auto mt-2 max-w-sm text-sm text-slate-600">
                    Your message has been sent to the CASIA team. We will respond from {CONTACT.email} shortly.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setDone(false); setValues(EMPTY); }}
                    data-testid="contact-send-another-button"
                    className="mt-6 rounded-md border border-navy px-6 py-3 text-xs font-bold uppercase tracking-wider text-navy transition-colors hover:bg-navy hover:text-white"
                  >
                    Send Another Message
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
